import { useEffect, useState } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';
import styled from 'styled-components';

import * as cctv from '../../utils/cctv';
import WebSdkRealtime from './WebSdkRealtime';
import { localCctvSettingState, userState } from '../../atoms';
import { bookmarkSelectBoxOptions } from '../../_constants';
import { CameraStatus, WebSDKcctv } from '../../utils/webSDKcctvClass';
import LeftSideMenu from './RealtimeCCTV/LeftSideMenu';
import { useFetchCctvList } from '../../services/useFetchCctvList';
import { apiGet } from '../../services/_common';
import { logPost } from '../../services/log';
import { toast } from 'react-toastify';
import LeftedToast from '../../components/cctv/LeftedToast';
import { useQuery } from '@tanstack/react-query';
import { t } from 'i18next';
import { useNavigate } from 'react-router-dom';

const Root = styled.div`
  width: 100vw;
  width: 100dvw;
  height: 100vh;
  height: 100dvh;
  display: flex;
  .main {
    flex: 1 0;
    display: flex;
    flex-direction: column;
    background-color: var(--black);
  }
  &.isDragging * {
    cursor: grabbing !important;
  }
`;

const Hcctv1 = () => {
  const userInfo = useRecoilValue(userState);
  const navigate = useNavigate();
  const { userId, hCd, sCd } = userInfo;
  const [visiblePanel, setVisiblePanel] = useState(true); // 왼쪽 사이드 패널 접힘/펼침 상태
  const [draggingCamInfo, setDraggingCamInfo] = useState<ISubMenu[] | null>(null); // 드래그한 카메라객체 정보
  const [droppedWndIndex, setDroppedWndIndex] = useState<null | number>(null); // 드래그한 카메라객체를 드랍한 윈도우 인덱스
  const [cctvOBJ, setCctvOBJ] = useState<WebSDKcctv>();
  const [isDragging, setIsDragging] = useState(false); // 마우스 드래그 상태 - 커서 모양 변경 용도
  const [initSelectedCamList, setInitSelectedCamList] = useState<{ type: string; camList: string; cdName: string }>(bookmarkSelectBoxOptions[0]);
  // cctvOBJ에서 선택한 카메라 상태
  const [selectedCctvCameraInfo, setSelectedCctvCameraInfo] = useState<SelectedCctvCameraInfo | null>(null);
  const [bookmarkedCams, setBookmarkedCams] = useState<IMainMenu[] | string>([]);
  const [isRecording, setIsRecording] = useState<boolean>(false);
  const [playingCamStatus, setPlayingCamStatus] = useState<any[]>([]);
  const [sdkInitLoading, setSdkInitLoading] = useState(true);
  const [camPlayLoading, setCamPlayLoading] = useState(false);
  const [integrationCamList, setIntegrationCamList] = useState<any>();
  const [cctvSettings, setCctvSettings] = useState<CctvSettings>();
  const [cctvDivisions, setCctvDivisions] = useState<number>(4);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [localCctvSettings, setLocalCctvSettings] = useRecoilState(localCctvSettingState);
  const [infraList, setInfraList] = useState<any[] | null>([]);
  const [nvrList, setNvrList] = useState<IMainMenu[]>([]);
  const [cameraInfo, setCameraInfo] = useState([]);
  const [userCamGrant, setUserCamGrant] = useState<IuserCamGrant>({
    capYn: 'N',
    ptzYn: 'N',
    recYn: 'N',
    recdownYn: 'N',
  });

  const headNvrQuery = useQuery(['headNvrGet', hCd], () => apiGet({ path: '/head/nvr', req: { hCd } }), { retry: 3 });
  const headCameraQuery = useQuery(['headCameraGet', hCd], () => apiGet({ path: '/head/camera', req: { hCd } }), { retry: 3 });
  const camGrantQuery = useQuery(['camGrantGet', hCd], () => apiGet({ path: '/cam/grant', req: { hCd } }), { retry: 3 });
  const { data: cctvYn } = useQuery([`headInfoGet${hCd}`, hCd], () => fetchHeadCctvYn(), {
    enabled: !!hCd && (sCd === '' || sCd === '00000'),
  });

  // 본사 CCTV 모니터링 사용여부
  const fetchHeadCctvYn = async () => {
    try {
      const req = { hCd };
      const res = await apiGet({ path: '/head/info', req });
      const result = res.data.data.headInfo.cctvYn === 'Y';
      return result;
    } catch (error) {
      console.error(error);
      throw new Error('error');
    }
  };

  const getCctvSettings = () => {
    apiGet({ path: '/user/cctv', req: { userId } }).then((res: any) => {
      if (res.data.data) {
        setCctvSettings(res.data.data);
      }
    });
  };

  useEffect(() => {
    if (cctv.getPluginOBJECT()?.oPlugin) {
      cctv.destroy();
    }
    getCctvSettings();
    logPost({
      hCd: userInfo.hCd,
      sCd: userInfo.sCd,
      userId: userInfo.userId,
      menu: 'CCTV 실시간 보기',
      action: '조회',
      etc: ``,
    });
  }, []);

  useEffect(() => {
    if (!headNvrQuery.isLoading && !headCameraQuery.isLoading && !headNvrQuery.isFetching && !headCameraQuery.isFetching) {
      const { nvrList: nl } = headNvrQuery.data.data.data;
      const { cameraList: cl } = headCameraQuery.data.data.data;
      setCameraInfo(cl);
      /**
       * 수정자 : 한영광
       * 수정일자 : 2024.8.16
       * 내용 : 현장과 본사에서 사용하는 카메라 채널에 대한 데이터의 키값(channelNum, cChannelNum)이 달라 동일한 키값(channelNum)으로 수정하는 로직을 추가 함
       */
      const newNvrList = nl.map((nvr: IMainMenu) => {
        return {
          ...nvr,
          subList: cl
            .filter((cam: any) => cam.nCd === nvr.nCd && cam.useYn === 'Y')
            .map((sub: ISubMenu) => ({
              ...sub,
              channelNum: sub.cChannelNum,
              isPlaying: false,
              isRecording: false,
            })),
        };
      });
      setNvrList(newNvrList);
      getInfraList(newNvrList);
    }
  }, [headNvrQuery.isLoading, headCameraQuery.isLoading, headNvrQuery.isFetching, headCameraQuery.isFetching]);

  const getInfraList = (paramNvrList: IMainMenu[]) => {
    const grouped: Record<string, IMainMenu[]> = {};
    paramNvrList.forEach((item) => {
      if (!grouped[item.infraCd]) grouped[item.infraCd] = [];
      grouped[item.infraCd].push(item);
    });

    // infraCd 값으로 정렬 후 cdSort를 사용하여 내부 정렬
    paramNvrList.sort((a, b) => {
      // 먼저 sInfra를 비교합. null이나 undefined는 빈 문자열로 처리
      if ((a.infraCd || '') === (b.infraCd || '')) {
        // sInfra가 동일할 경우 cdSort를 비교. null인경우 가장 마지막으로 보냄
        const sortA = a.cdSort === null ? Number.MAX_SAFE_INTEGER : a.cdSort;
        const sortB = b.cdSort === null ? Number.MAX_SAFE_INTEGER : b.cdSort;
        return sortA - sortB;
      }
      // infraCd 기본 비교, null 또는 undefined는 빈 문자열로 처리
      return (a.infraCd || '')?.localeCompare(b.infraCd || '');
    });

    // 그룹화된 데이터를 사용하여 infraCd 리스트 생성
    const groupArray = Object.keys(grouped)
      .sort((a, b) => a.localeCompare(b)) // infraCd 오름차순정렬
      .map((key) => ({
        infraCd: key,
        infraName: grouped[key][0].infraName,
        cdsortKey: grouped[key][0].cdSort,
        expand: false,
      }));
    setInfraList(groupArray);
  };

  /**
   * 작성자 : 한영광
   * 작성일자 : 2024.8.16
   * 내용 : 사용자 카메라 기능 사용 권한에 따라 해당 기능 버튼 비활성화 처리를 위해 카메라 권한 데이터가 조회되면 사용자의 권한 상태값을 수정
   *        (마스터 관리자, 본사 관리자 사용자인 경우 모든 기능 사용 가능하도록 처리)
   */
  useEffect(() => {
    if (!camGrantQuery.isLoading && !camGrantQuery.isFetching) {
      const filteredUserCamGrant = camGrantQuery.data.data.data.filter((camgrant: any) => camgrant.useYn === 'Y').find((camgrant: any) => userInfo.camGrant === camgrant.gCd);
      if (filteredUserCamGrant) {
        setUserCamGrant(filteredUserCamGrant);
      }
      if (userInfo.gCd === 'MM' || userInfo.gCd === 'HM') {
        setUserCamGrant({
          capYn: 'Y',
          ptzYn: 'Y',
          recYn: 'Y',
          recdownYn: 'Y',
        });
      }
    }
  }, [camGrantQuery.isLoading, camGrantQuery.isFetching]);

  useEffect(() => {
    setCctvDivisions(Number(localCctvSettings.camCType));
  }, [localCctvSettings.camCType]);

  useEffect(() => {
    if (cctvSettings) {
      setCctvDivisions(Number(cctvSettings.camCType));
    }
    if (cctvSettings?.camList1) {
      setBookmarkedCams(JSON.parse(cctvSettings?.camList1));
      setInitSelectedCamList(bookmarkSelectBoxOptions[2]);
    } else if (cctvSettings?.camList2) {
      setBookmarkedCams(JSON.parse(cctvSettings?.camList2));
      setInitSelectedCamList(bookmarkSelectBoxOptions[3]);
    } else if (cctvSettings?.camList3) {
      setBookmarkedCams(JSON.parse(cctvSettings?.camList3));
      setInitSelectedCamList(bookmarkSelectBoxOptions[4]);
    } else if (cctvSettings?.camList4) {
      setBookmarkedCams(JSON.parse(cctvSettings?.camList4));
      setInitSelectedCamList(bookmarkSelectBoxOptions[5]);
    } else if (cctvSettings?.camList5) {
      setBookmarkedCams(JSON.parse(cctvSettings?.camList5));
      setInitSelectedCamList(bookmarkSelectBoxOptions[6]);
    }
  }, [integrationCamList, cctvSettings, cctvOBJ]);

  useEffect(() => {
    if (camPlayLoading) {
      toast(<LeftedToast message='기다려 주세요..' />, { position: 'top-left', autoClose: 20000 });
    } else {
      toast.dismiss();
    }
  }, [camPlayLoading]);

  useEffect(() => {
    if (cctvYn !== undefined && cctvYn === false) {
      navigate(-1);
      toast.warning(t('해당 페이지의 권한이 없습니다. 담당자에게 문의하세요.'));
    }
  }, [cctvYn]);

  // RealtimeCCTV 컴포넌트에서 cctvSettings가 undefined일 때의 처리 추가
  if (!cctvSettings || !cctvYn) {
    return null;
  }

  return (
    <Root className={isDragging ? 'isDragging' : ''}>
      <LeftSideMenu
        cctvOBJ={cctvOBJ}
        visiblePanel={visiblePanel}
        setDraggingCamInfo={setDraggingCamInfo}
        nvrList={nvrList}
        setNvrList={setNvrList}
        infraList={infraList}
        setInfraList={setInfraList}
        isLoading={headNvrQuery.isError || headCameraQuery.isError}
        isError={headNvrQuery.isLoading || headCameraQuery.isLoading || headNvrQuery.isFetching || headCameraQuery.isFetching}
        setIsDragging={setIsDragging}
        cctvSettings={cctvSettings || null}
        bookmarkedCams={bookmarkedCams}
        setBookmarkedCams={setBookmarkedCams}
        initSelectedCamList={initSelectedCamList}
        setInitSelectedCamList={setInitSelectedCamList}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        selectedCctvCameraInfo={selectedCctvCameraInfo}
        setSelectedCctvCameraInfo={setSelectedCctvCameraInfo}
        cctvDivisions={cctvDivisions}
        setCctvDivisions={setCctvDivisions}
        isRecording={isRecording}
        setIsRecording={setIsRecording}
        playingCamStatus={playingCamStatus}
        setPlayingCamStatus={setPlayingCamStatus}
        sdkInitLoading={sdkInitLoading}
        setSdkInitLoading={setSdkInitLoading}
        camPlayLoading={camPlayLoading}
        setCamPlayLoading={setCamPlayLoading}
        integrationCamList={integrationCamList}
        userCamGrant={userCamGrant}
        setDroppedWndIndex={setDroppedWndIndex} // 드래그한 카메라 객체를 드랍한 윈도우 인덱스 변경 함수
      />
      <div className='main'>
        <WebSdkRealtime
          cctvOBJ={cctvOBJ} // cctv 객체
          setCctvOBJ={setCctvOBJ} // cctv 객체 변경 함수
          isLoading={headNvrQuery.isLoading || headCameraQuery.isLoading || headNvrQuery.isFetching || headCameraQuery.isFetching}
          draggingCamInfo={draggingCamInfo} // 드래그한 카메라 객체 정보
          setDraggingCamInfo={setDraggingCamInfo} // 드래그한 카메라 객체 정보 변경 함수
          droppedWndIndex={droppedWndIndex} // 드래그한 카메라 객체를 드랍한 윈도우 인덱스
          setDroppedWndIndex={setDroppedWndIndex} // 드래그한 카메라 객체를 드랍한 윈도우 인덱스 변경 함수
          cameraInfo={cameraInfo}
          setIsDragging={setIsDragging} // 드래그 상태 변경 함수
          visiblePanel={visiblePanel} // 왼쪽 사이드 패널 접기/펼치기 상태
          setVisiblePanel={setVisiblePanel} // 왼쪽 사이드 패널 상태 변경 함수
          nvrList={nvrList}
          setNvrList={setNvrList}
          cctvSettings={cctvSettings || null}
          bookmarkedCams={bookmarkedCams} // 즐겨찾기 선택한 카메라목록
          setBookmarkedCams={setBookmarkedCams} // 즐겨찾기 카메라목록 상태값변경
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          selectedCameraInfo={selectedCctvCameraInfo}
          setSelectedCameraInfo={setSelectedCctvCameraInfo}
          isRecording={isRecording}
          setIsRecording={setIsRecording}
          cctvDivisions={cctvDivisions}
          setCctvDivisions={setCctvDivisions}
          playingCamStatus={playingCamStatus}
          setPlayingCamStatus={setPlayingCamStatus}
          sdkInitLoading={sdkInitLoading}
          setSdkInitLoading={setSdkInitLoading}
          camPlayLoading={camPlayLoading}
          setCamPlayLoading={setCamPlayLoading}
          integrationCamList={integrationCamList}
          userCamGrant={userCamGrant}
        />
      </div>
    </Root>
  );
};

export default Hcctv1;
